import CONSTANTS from 'src/utils/constants';
import { CalculationBuilderState } from 'src/services/calculation-builder/CalculationBuilderState';

const { EDITABLE, READ_ONLY, FROZEN } = CONSTANTS.VIEW_MODE;

export function isCalculationFrozen(
    state: CalculationBuilderState,
    canEdit: boolean,
): boolean {
    if (!canEdit) return true;
    if (state.calculationExecution?.calculationExecutionId != null) return true;
    const s = state.calculationTemplateMetaData?.calculationStatus;
    return s === CONSTANTS.CALCULATION_STATUS.INACTIVE
        || s === CONSTANTS.CALCULATION_STATUS.TERMINATED;
}

function isPendingReview(state: CalculationBuilderState): boolean {
    return state.calculationTemplateMetaData?.calculationStatus === CONSTANTS.CALCULATION_STATUS.PENDING_REVIEW;
}

/**
 * View mode for the CLI-Config lock session. Owns Steps 4-6 (JE settings,
 * Review journals, PLI). Also owns Steps 1-3 grids for standalone (non-
 * template-linked) CLIs — consumers pick between this and
 * getCliTemplateViewMode based on isTemplateLinked at the call site.
 *
 * Flag states:
 * - null (still loading): READ_ONLY, so we never briefly render an editable
 *   CLI before the flag resolves.
 * - false (flag disabled): EDITABLE — pre-feature behavior where any
 *   non-frozen calc was directly editable.
 * - true (flag enabled): defer to state.cliConfigViewMode, which
 *   useLockActions maintains and CalculationBuilderView reconciles from
 *   metadata.lockInfo.
 */
export function getCliConfigViewMode(
    state: CalculationBuilderState,
    canEdit: boolean,
    editLockingEnabled: boolean | null,
): string {
    if (isCalculationFrozen(state, canEdit)) return FROZEN;
    if (isPendingReview(state)) return READ_ONLY;
    if (editLockingEnabled === null) return READ_ONLY;
    if (editLockingEnabled === false) return EDITABLE;
    return state.cliConfigViewMode || READ_ONLY;
}

/**
 * View mode for the CLI-Template edit session. Owns Steps 1-3 grids (data
 * sources + calc steps) for TEMPLATE-LINKED CLIs. Never used for standalone
 * CLIs (consumers pick getCliConfigViewMode when isTemplateLinked is false).
 *
 * A template edit session is a separate backend lock from the CLI-Config
 * lock — it's stored per template (metadata.templateEditCalculation), not
 * per calc. This selector reads state.cliTemplateViewMode, which
 * TemplatesContainer keeps synchronized with metadata via a reconciler
 * effect.
 */
export function getCliTemplateViewMode(
    state: CalculationBuilderState,
    canEdit: boolean,
): string {
    if (isCalculationFrozen(state, canEdit)) return FROZEN;
    if (isPendingReview(state)) return READ_ONLY;
    return state.cliTemplateViewMode || READ_ONLY;
}

/**
 * True when a template edit session is active for this calc.
 * Consumers use this for the "Editing template" banner + the Cancel /
 * Confirm template-action-bar variant + the Steps 3 Cancel-button gate.
 */
export function isTemplateEditActive(
    state: CalculationBuilderState,
    canEdit: boolean,
): boolean {
    return getCliTemplateViewMode(state, canEdit) === EDITABLE;
}

/**
 * True when the CLI-Config edit-locking UI should render at all: flag is
 * enabled AND this is a real (non-new, non-ICRS) calc. Gates the
 * EditLockButtonBar, LockBanner, and the metadata-driven lockInfo
 * reconciler in CalculationBuilderView.
 */
export function getEditLockingApplies(
    editLockingEnabled: boolean | null,
    isNewCalculation: boolean,
    isICRSRecord: boolean,
): boolean {
    return editLockingEnabled === true && !isNewCalculation && !isICRSRecord;
}
